/**
 * 爱微圈导航 · 设置应用与绑定
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, gateGuest, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export function applySettings() {
    const s = state.settings;
    const root = document.documentElement;
    // 壁纸
    const layer = $('#wpLayer');
    layer.style.filter = '';
    let wallImg = null;
    if (s.wallpaper_type === 'color') {
        layer.style.backgroundImage = 'none';
        layer.style.backgroundColor = s.wallpaper_color || '#4A90D9';
    } else if (s.wallpaper_type === 'bing' && state.bingWallpaper) {
        wallImg = state.bingWallpaper;
    } else if (s.wallpaper_type === 'url' && s.wallpaper_url) {
        wallImg = s.wallpaper_url;
    } else {
        layer.style.backgroundColor = '';
        layer.style.backgroundImage = s.wallpaper_gradient || 'linear-gradient(160deg,#0f2027,#203a43,#2c5364)';
    }
    if (wallImg) {
        /* 图片壁纸：预加载成功后再淡入切换，加载期间保留深色兜底，避免露白闪屏 */
        const url = String(wallImg).replace(/"/g, '%22');
        layer.style.backgroundColor = '#1a1d26';
        const img = new Image();
        img.onload = () => { layer.style.backgroundImage = `url("${url}")`; };
        img.onerror = () => { layer.style.backgroundColor = '#1a1d26'; };
        img.src = wallImg;
    }
    const mask = $('#wpMask');
    mask.style.background = `rgba(0,0,0,${Math.min(0.85, (parseInt(s.mask_opacity) || 0) / 100)})`;
    const blur = parseInt(s.blur_amount) || 0;
    layer.style.filter = blur > 0 ? `blur(${blur / 3}px)` : '';

    // 模式
    document.body.dataset.mode = (s.mode === 'minimal') ? 'minimal' : (s.mode === 'detailed') ? 'detailed' : 'standard';

    // 图标
    root.style.setProperty('--icon-size', (parseInt(s.icon_size) || 64) + 'px');
    root.style.setProperty('--icon-radius', (parseInt(s.icon_radius) ?? 50) + '%');
    root.style.setProperty('--font-size-icon', (parseInt(s.font_size) || 14) + 'px');
    root.style.setProperty('--icon-opacity', (parseInt(s.icon_opacity) || 100) / 100);
    root.style.setProperty('--icon-font-size', (parseInt(s.icon_font_size) || 14) + 'px');

    // 字体
    root.style.setProperty('--text', s.font_color || '#ffffff');
    const fontColorValue = $('#fontColorValue');
    if (fontColorValue) fontColorValue.textContent = (s.font_color || '#ffffff').toUpperCase();
    document.body.classList.toggle('no-font-shadow', s.font_shadow === '0');

    // 同步设置面板开关（即使面板尚未打开也同步，避免打开后状态滞后）
    $$('[data-toggle]').forEach(el => el.classList.toggle('on', s[el.dataset.toggle] === '1'));
    $$('[data-setting]').forEach(el => {
        if (el.type === 'range' || el.tagName === 'SELECT' || el.type === 'color') {
            el.value = s[el.dataset.setting] ?? el.value;
        }
    });

    // 搜索框
    const searchWrap = $('#searchWrap');
    searchWrap.style.display = s.hide_search === '1' ? 'none' : '';
    searchWrap.style.width = `calc(min(620px, 92vw) * ${(parseInt(s.search_size) || 100) / 100})`;
    $('.search-bar').style.borderRadius = (parseInt(s.search_radius) ?? 24) + 'px';
    $('.search-bar').style.opacity = (parseInt(s.search_opacity) || 100) / 100;
    $('#searchTabs').style.display = s.hide_search_category === '1' ? 'none' : 'flex';
    document.body.dataset.hideSearchBtn = s.hide_search_btn === '1' ? '1' : '0';
    const searchGo = $('#searchSubmit');
    if (searchGo) searchGo.style.display = s.hide_search_btn === '1' ? 'none' : '';

    // 显示开关：时钟/天气用 visibility 保留占位，防止布局塌陷
    if ($('#heroClock')) {
        $('#heroClock').style.display = '';
        $('#heroClock').style.visibility = s.show_clock === '0' ? 'hidden' : '';
        $('#heroClock').style.pointerEvents = s.show_clock === '0' ? 'none' : '';
    }
    if ($('#clock')) {
        $('#clock').style.display = '';
        $('#clock').style.visibility = s.show_clock === '0' ? 'hidden' : '';
        $('#clock').style.pointerEvents = s.show_clock === '0' ? 'none' : '';
    }
    // 天气：用户开关 + 后台全站总开关（任一关闭即隐藏）
    const weatherOff = s.show_weather === '0' || state.site.weather_enabled === '0';
    $('#weatherChip').style.display = weatherOff ? 'none' : '';
    $('#weatherChip').style.visibility = '';
    $('#weatherChip').style.pointerEvents = '';
    if ($('#btnHome')) $('#btnHome').style.display = s.hide_home_btn === '1' ? 'none' : '';
    $('#btnTodo').style.display = s.show_todo === '0' ? 'none' : '';
    $('#btnNotes').style.display = s.show_notes === '0' ? 'none' : '';
    /* 卡片模式是「全量视图」：一次拉取并展示所有页的快捷方式，本身不分页。
     * 此时页码/箭头点了也不会切换视图（renderCardViewAll 有 60s 缓存），只会误导用户 → 直接隐藏。 */
    const isCardMode = s.display_mode === 'card';
    $('#pageIndicator').style.display = isCardMode ? 'none' : '';
    $$('.page-arrow').forEach(el => el.style.display = (isCardMode || s.show_page_btn === '0') ? 'none' : '');
    const cardHint = $('#cardModeHint');
    if (cardHint) cardHint.style.display = isCardMode ? '' : 'none';
    const showWp = s.show_wallpaper_btn === '1';
    const showWind = s.show_windmill === '1';
    if ($('#btnWallpaper')) $('#btnWallpaper').style.display = showWp ? '' : 'none';
    if ($('#windmill')) $('#windmill').style.display = showWind ? '' : 'none';
    if ($('#cornerActions')) $('#cornerActions').style.display = (showWp || showWind) ? '' : 'none';
    const filing = $('#filing');
    if (filing) filing.style.display = s.hide_filing === '1' ? 'none' : '';

    // 屏幕缩放
    const zoom = (parseInt(s.screen_zoom) || 100) / 100;
    $('#mainArea').style.zoom = '';
    $('#mainArea').style.transform = zoom !== 1 ? `scale(${zoom})` : '';
    $('#mainArea').style.transformOrigin = 'top center';
    // 侧边栏缩放
    const sidebarZoom = (parseInt(s.sidebar_zoom) || 100) / 100;
    const sidebar = $('#sidebar');
    if (sidebar) sidebar.style.transform = sidebarZoom !== 1 ? `scale(${sidebarZoom})` : '';
    if (sidebar) sidebar.style.transformOrigin = 'top right';
}
// 上次已成功保存的 settings 快照：scheduleSave 只 POST 相对它的变更字段（P1-2.2），
// 避免每次把整个 settings 对象（60+ 键）原样回传，减少写放大与网络/DB 开销。

let lastSavedSettings = {};

export const scheduleSave = debounce(async () => {
    // 游客不持久化设置：改动仅本次会话生效（本地即时预览），同时引导登录（2026-09-04 策略）
    if (gateGuest()) return;
    const diff = {};
    for (const k in state.settings) {
        if (state.settings[k] !== lastSavedSettings[k]) diff[k] = state.settings[k];
    }
    if (!Object.keys(diff).length) return;
    try {
        await apiPost(API.settings + '?action=save', diff);
        lastSavedSettings = { ...state.settings };
    } catch (e) { toast('设置保存失败，请检查网络'); }
}, 500);


export async function loadSettings() {
    try {
        const remote = await apiGet(API.settings + '?action=get');
        state.settings = { ...DEFAULT_SETTINGS, ...remote };
    } catch (e) { state.settings = { ...DEFAULT_SETTINGS }; }
    lastSavedSettings = { ...state.settings }; // 建立保存基线，首屏加载后不再整量回传
    // 同步 display_mode：mode 为 detailed 时一定是 card
    if (state.settings.mode === 'detailed') state.settings.display_mode = 'card';
    if (state.settings.wallpaper_type === 'bing') {
        try {
            const d = await apiGet(API.wallpaper + '?action=daily');
            if (d.url) { state.bingWallpaper = d.url; state.settings.wallpaper_credit = d.credit || ''; }
        } catch (e) {
            /* 拉取失败但缓存已有当日图片时，沿用缓存壁纸，避免闪屏后回落纯色 */
            if (!state.bingWallpaper) state.settings.wallpaper_type = 'gradient';
        }
    }
    applySettings();
    renderEngineDropdown();
    if (state.searchTab) applySearchTabUI();
    /* 刷新防闪屏：远端设置没改变渲染结果（指纹一致）时跳过重渲染 */
    if (state.settings.display_mode === 'card') {
        renderShortcuts();
    } else if (state._renderedFp !== gridRenderFp()) {
        renderShortcuts();
    }
    cacheState(); // 更新缓存
    /* 天气延迟加载：首屏 6 个关键 API（settings/shortcuts/categories/todos/wallpaper/site_info）
     * 抢满浏览器对同一域名的并发连接槽后，再异步拉天气，避免天气上游慢请求阻塞其他接口。 */
    const startWeather = () => loadWeather();
    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(startWeather, { timeout: 2000 });
    } else {
        setTimeout(startWeather, 500);
    }
}

export function bindSettingsUI() {
    const s = state.settings;
    $$('[data-setting]').forEach(el => {
        const key = el.dataset.setting;
        if (el.type === 'range') {
            el.value = s[key] ?? el.value;
            const valEl = el.parentElement.querySelector('.slider-value');
            if (valEl) valEl.textContent = el.value + (el.parentElement.dataset.unit || (key.includes('size') || key.includes('font') || key.includes('blur') || key.includes('opacity') || key.includes('zoom') || key.includes('radius') ? (key === 'font_size' || key === 'icon_size' || key === 'search_radius' ? 'px' : '%') : '%'));
        } else if (el.tagName === 'SELECT' || el.type === 'color') {
            el.value = s[key] ?? el.value;
        } else if (el.type === 'text') {
            el.value = s[key] ?? '';
        }
        el.oninput = () => {
            // display_mode 由下方专用 onchange 处理，避免与通用 oninput 重复设置/保存，
            // 也避免卡片↔网格切换时因 state.shortcuts 数据范围不一致导致渲染异常。
            if (key === 'display_mode') return;
            state.settings[key] = el.value;
            if (el.type === 'range') {
                const valEl = el.parentElement.querySelector('.slider-value');
                if (valEl) valEl.textContent = el.value + (valEl.dataset.unit || '');
            }
            if (el.type === 'color') {
                const cvEl = el.closest('.color-picker') ? el.closest('.color-picker').querySelector('.color-value') : null;
                if (cvEl) cvEl.textContent = el.value.toUpperCase();
            }
            applySettings();
            if (key === 'grid_cols' || key === 'grid_rows') {
                // 网格变化时立即按新容量重新分页，并清空旧容量缓存
                state.shortcutCache = {};
                apiPost(API.shortcuts + '?action=rebalance_pages', {
                    grid_cols: state.settings.grid_cols,
                    grid_rows: state.settings.grid_rows,
                }).then(() => loadShortcuts()).catch(() => renderShortcuts());
            }
            if (key === 'language' && window.i18n) {
                const langMap = { 'en': 'en-US', 'zh-CN': 'zh-CN', 'zh-TW': 'zh-TW' };
                window.i18n.setLang(langMap[el.value] || el.value);
            }
            // 卡片列数变化必须立即重渲染，否则要等刷新才生效
            if (key === 'card_cols') renderShortcuts();
            // 时钟下方显示模式切换：立即拉取对应每日内容并更新显示
            if (key === 'daily_greet_mode') loadDailyContent(el.value);
            scheduleSave();
        };
        if (el.type === 'text') {
            el.onchange = () => {
                state.settings[key] = el.value;
                applySettings();
                if (key === 'weather_city') loadWeather();
                scheduleSave();
            };
        }
    });

    // 壁纸类型切换：显示/隐藏对应行
    const wpTypeSelect = $('[data-setting="wallpaper_type"]');
    if (wpTypeSelect) {
        const updateWpRows = () => {
            const type = wpTypeSelect.value;
            const colorRow = $('.wp-row-color');
            const gradientRow = $('.wp-row-gradient');
            const urlRow = $('.wp-row-url');
            if (colorRow) colorRow.style.display = type === 'color' ? '' : 'none';
            if (gradientRow) gradientRow.style.display = type === 'gradient' ? '' : 'none';
            if (urlRow) urlRow.style.display = type === 'url' ? '' : 'none';
        };
        updateWpRows();
        wpTypeSelect.onchange = () => {
            state.settings.wallpaper_type = wpTypeSelect.value;
            updateWpRows();
            if (wpTypeSelect.value === 'bing') {
                apiGet(API.wallpaper + '?action=daily').then(d => {
                    if (d.url) { state.bingWallpaper = d.url; state.settings.wallpaper_credit = d.credit || ''; }
                    applySettings();
                    scheduleSave();
                }).catch(() => {});
            } else {
                applySettings();
                scheduleSave();
            }
        };
    }

    // 显示模式切换：显示/隐藏卡片相关设置
    const displayModeSelect = $('[data-setting="display_mode"]');
    if (displayModeSelect) {
        const updateDisplayRows = () => {
            const mode = displayModeSelect.value;
            const descRow = $('#cardDescRow');
            const marqueeRow = $('#cardMarqueeRow');
            const colsRow = $('#cardColsRow');
            const isCard = mode === 'card';
            if (descRow) descRow.style.display = isCard ? '' : 'none';
            if (marqueeRow) marqueeRow.style.display = isCard ? '' : 'none';
            if (colsRow) colsRow.style.display = isCard ? '' : 'none';
        };
        updateDisplayRows();
        // 显示模式切换做专门处理：
        // 1. 卡片模式使用 all_shortcuts（跨页全量），网格模式使用 list&page=current（仅当前页）。
        // 2. 从卡片切回网格时，state.shortcuts 还是全量，直接 renderShortcuts() 会把所有页铺在一页，
        //    导致卡顿甚至假死。必须重新拉取当前页数据。
        // 3. 离开卡片模式时把 60s 全量缓存失效，下次再进卡片会重新拉取。
        displayModeSelect.onchange = () => {
            const newMode = displayModeSelect.value;
            state.settings.display_mode = newMode;
            updateDisplayRows();
            applySettings();
            if (newMode === 'card') {
                renderCardViewAll();
                scheduleSave();
            } else {
                state._cardAllAt = 0; // 离开卡片模式：全量缓存失效
                state.shortcutCache = {}; // 网格容量可能和缓存里不一致，全部清掉重拉
                // 卡片模式无分页概念，切回网格时按当前网格容量整体重排，避免某些页数据溢出
                apiPost(API.shortcuts + '?action=rebalance_pages', {
                    grid_cols: state.settings.grid_cols,
                    grid_rows: state.settings.grid_rows,
                }).then(() => loadShortcuts()).catch(() => loadShortcuts());
                scheduleSave();
            }
        };
    }

    // 渐变预设
    const gradientBox = $('#gradientPresets');
    if (gradientBox) {
        const presets = [
            'linear-gradient(135deg,#0c0c1d,#1a1a3a,#2d6a4f,#5f4b8b,#00c9ff)',
            'linear-gradient(160deg,#0f2027,#203a43,#2c5364)',
            'linear-gradient(135deg,#667eea,#764ba2)',
            'linear-gradient(135deg,#f093fb,#f5576c)',
            'linear-gradient(135deg,#4facfe,#00f2fe)',
            'linear-gradient(135deg,#43e97b,#38f9d7)',
            'linear-gradient(135deg,#fa709a,#fee140)',
            'linear-gradient(135deg,#30cfd0,#330867)',
            'linear-gradient(135deg,#a8edea,#fed6e3)',
            'linear-gradient(160deg,#1a1a2e,#16213e,#0f3460)',
            'linear-gradient(160deg,#2c1810,#3e2723,#5d4037)',
            'linear-gradient(160deg,#1b1b3a,#373a6c,#5d5ae0)',
            'linear-gradient(135deg,#0c0c1d,#1a1a3a,#2c5364)',
        ];
        gradientBox.innerHTML = presets.map(g =>
            `<div class="gradient-preset ${state.settings.wallpaper_gradient === g ? 'active' : ''}" data-g="${escAttr(g)}" style="background:${escAttr(g)}"></div>`
        ).join('');
        $$('.gradient-preset', gradientBox).forEach(el => {
            el.onclick = () => {
                state.settings.wallpaper_gradient = el.dataset.g;
                state.settings.wallpaper_type = 'gradient';
                $$('.gradient-preset', gradientBox).forEach(e => e.classList.toggle('active', e === el));
                applySettings();
                scheduleSave();
            };
        });
    }
    $$('[data-toggle]').forEach(tg => {
        const key = tg.dataset.toggle;
        tg.classList.toggle('on', s[key] === '1');
        tg.onclick = () => {
            const v = tg.classList.contains('on') ? '0' : '1';
            state.settings[key] = v;
            tg.classList.toggle('on', v === '1');
            applySettings();
            if (key === 'hide_icon_name' || key === 'startup_animation') renderShortcuts();
            if (key === 'show_todo_count') renderTodos();
            if (key === 'show_weather') loadWeather();
            scheduleSave();
        };
    });
    // 网格预设
    $$('.grid-option').forEach(g => {
        const [r, c] = (g.dataset.grid || '').split('x').map(Number);
        g.classList.toggle('active', g.dataset.grid !== 'custom' && r === parseInt(s.grid_rows) && c === parseInt(s.grid_cols));
        g.onclick = () => {
            if (g.dataset.grid === 'custom') {
                $('.custom-grid-input').style.display = 'flex';
                $('#customRows').value = parseInt(s.grid_rows) || 3;
                $('#customCols').value = parseInt(s.grid_cols) || 3;
                return;
            }
            state.settings.grid_rows = String(r);
            state.settings.grid_cols = String(c);
            state.settings.card_cols = String(c);
            $$('.grid-option').forEach(x => x.classList.toggle('active', x === g));
            $('.custom-grid-input').style.display = 'none';
            applySettings(); renderShortcuts(); scheduleSave();
            // 网格容量变化：立即按新容量重新分页，并清空旧容量缓存避免切页时显示错乱的页数据
            state.shortcutCache = {};
            apiPost(API.shortcuts + '?action=rebalance_pages', {
                grid_cols: state.settings.grid_cols,
                grid_rows: state.settings.grid_rows,
            }).then(() => loadShortcuts()).catch(() => renderShortcuts());
        };
    });
    $('#applyCustomGrid').onclick = () => {
        state.settings.grid_rows = String(Math.max(1, Math.min(6, +$('#customRows').value || 3)));
        state.settings.grid_cols = String(Math.max(3, Math.min(10, +$('#customCols').value || 6)));
        state.settings.card_cols = state.settings.grid_cols;
        applySettings(); renderShortcuts(); scheduleSave();
        state.shortcutCache = {};
        apiPost(API.shortcuts + '?action=rebalance_pages', {
            grid_cols: state.settings.grid_cols,
            grid_rows: state.settings.grid_rows,
        }).then(() => loadShortcuts()).catch(() => renderShortcuts());
        toast('网格布局已更新', 'success');
    };
    // 模式
    $$('.mode-tab').forEach(m => {
        m.classList.toggle('active', m.dataset.mode === s.mode);
        m.onclick = () => {
            state.settings.mode = m.dataset.mode;
            // 详细模式 → 卡片显示；其他 → 网格显示
            state.settings.display_mode = (m.dataset.mode === 'detailed') ? 'card' : 'grid';
            $$('.mode-tab').forEach(x => x.classList.toggle('active', x === m));
            // 同步显示模式下拉框
            const dmSelect = $('[data-setting="display_mode"]');
            if (dmSelect) dmSelect.value = state.settings.display_mode;
            // 显示/隐藏卡片相关设置
            const isCard = state.settings.display_mode === 'card';
            const descRow = $('#cardDescRow');
            const marqueeRow = $('#cardMarqueeRow');
            const colsRow = $('#cardColsRow');
            if (descRow) descRow.style.display = isCard ? '' : 'none';
            if (marqueeRow) marqueeRow.style.display = isCard ? '' : 'none';
            if (colsRow) colsRow.style.display = isCard ? '' : 'none';
            applySettings(); renderShortcuts(); scheduleSave();
        };
    });
    // 初始化时同步 display_mode
    if (s.mode === 'detailed') state.settings.display_mode = 'card';
    $('#btnResetSettings').onclick = async () => {
        const ok = await modalConfirm('恢复默认设置', '所有个性化设置将重置为默认值，确认继续？');
        if (!ok) return;
        try {
            await apiPost(API.settings + '?action=reset', {});
            state.settings = { ...DEFAULT_SETTINGS };
            applySettings(); renderShortcuts(); bindSettingsUI();
            toast('已恢复默认设置', 'success');
        } catch (e) { toast(e.message, 'error'); }
    };
}

/* ---------- 壁纸选择器 ---------- */

